/**
 * The legal-document read (`docs/design/00-contracts.md` §6.23, §9.5, §13.3; C-2, C-30).
 *
 * §13.3 requires `POST /v1/organizations` to carry `acceptedDocuments: [{ kind, termsVersionId }]`
 * for `customer_terms` **and** `dpa`, and `POST /v1/auth/session` the same for `user_terms` and
 * `privacy_policy`. C-2 and C-30 forbid a `LEGAL_DOCUMENTS` constant and a `TERMS_VERSION_*` env
 * var precisely so the accepted document is a database fact rather than a copy. Those statements
 * only hold together if the client can *read* which row is current — this is that read, and
 * nothing more.
 *
 * It exposes platform-level rows only. `terms_versions` is the one table whose SELECT policy is
 * `USING (true)`, it carries no `organization_id`, and every column here is already published at
 * `document_url`. The response reuses `pendingConsentSchema`, so the shape the consent gate
 * renders and the shape the sign-up and create-organization forms render are the same shape.
 */
import { z } from 'zod';

import { pendingConsentSchema } from './auth.js';
import { termsKindSchema } from '../enums.js';
import { localeSchema } from '../locale.js';

export const currentTermsQuerySchema = z
  .object({
    /** Which language's rows to return. Both `nb` and `en` exist for every version. */
    locale: localeSchema.optional(),
  })
  .meta({ id: 'CurrentTermsQuery' });

/**
 * Et nytt utkast som ikke gjelder ennå (279.11 b: «rett inn i appen, merket utkast»): den nyeste
 * versjonen som er nyere enn den gjeldende, og som har en tekst å vise. Uten den var 2026-10-26 bare
 * å nå ved å skrive adressen, mens den gjeldende (2026-08-01) ikke har tekst.
 */
export const legalDraftSchema = z
  .object({
    kind: termsKindSchema,
    version: z.string(),
    documentUrl: z.string(),
  })
  .meta({ id: 'LegalDraft' });
export type LegalDraft = z.infer<typeof legalDraftSchema>;

export const currentTermsResponseSchema = z
  .object({
    documents: z.array(pendingConsentSchema),
    /** Utkastene, ett per dokument som har et. `default` så et eldre svar uten feltet leses som ingen. */
    drafts: z.array(legalDraftSchema).default([]),
  })
  .meta({ id: 'CurrentTermsResponse' });

export type CurrentTermsQuery = z.infer<typeof currentTermsQuerySchema>;
export type CurrentTermsResponse = z.infer<typeof currentTermsResponseSchema>;

// ─── Teksten (QA13 punkt 9, sak 253.83) ─────────────────────────────────────────────────────────

/**
 * Én blokk av et juridisk dokument. Fila er et lite utsnitt av Markdown (`#`, `##`, `###`, `- `,
 * `> `, avsnitt), og API-et sender blokkene, så nettet og en app tegner det samme uten å tolke
 * Markdown selv. `1. ` er en nummerert liste. `**fet**` står igjen i teksten og tegnes av klienten.
 */
export const legalBlockSchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('heading'), level: z.union([z.literal(2), z.literal(3)]), text: z.string() }),
    z.object({ kind: z.literal('paragraph'), text: z.string() }),
    /**
     * `- ` eller `1. `. `ordered` er valgfri, så en klient som ikke kjenner den, tegner punktene som
     * en vanlig liste i stedet for å miste dem (brukervilkårene 2026-10-26, AI-delen).
     */
    z.object({ kind: z.literal('list'), items: z.array(z.string()), ordered: z.literal(true).optional() }),
    /** `> …`: merknaden øverst («Utkast — skal gjennomgås av advokat»). */
    z.object({ kind: z.literal('notice'), text: z.string() }),
    /** `| a | b |`: lagringstider og underleverandører (revisjon 0.1). Første rad er overskriften. */
    z.object({ kind: z.literal('table'), header: z.array(z.string()), rows: z.array(z.array(z.string())) }),
  ])
  .meta({ id: 'LegalBlock' });
export type LegalBlock = z.infer<typeof legalBlockSchema>;

export const legalDocumentParamsSchema = z
  .object({
    kind: termsKindSchema,
    version: z.string().regex(/^[0-9A-Za-z][0-9A-Za-z.-]{0,39}$/),
  })
  .meta({ id: 'LegalDocumentParams' });
export type LegalDocumentParams = z.infer<typeof legalDocumentParamsSchema>;

export const legalDocumentQuerySchema = z
  .object({ locale: localeSchema.optional() })
  .meta({ id: 'LegalDocumentQuery' });
export type LegalDocumentQuery = z.infer<typeof legalDocumentQuerySchema>;

/**
 * Ett dokument i én versjon og ett språk, med teksten samtykket peker på. `body` er `null` for en
 * versjon som ble publisert uten tekst (2026-08-01): raden og samtykkene står, men det fantes ingen
 * tekst å vise.
 */
export const legalDocumentDtoSchema = z
  .object({
    termsVersionId: z.uuid(),
    kind: termsKindSchema,
    version: z.string(),
    locale: localeSchema,
    title: z.string(),
    effectiveFrom: z.iso.datetime(),
    /** Er dette den gjeldende versjonen? */
    current: z.boolean(),
    /** Et utkast som ikke gjelder ennå: ikke gjeldende, men nyere enn den som gjelder (sak 255.4). */
    pending: z.boolean(),
    /** Adressen til den gjeldende versjonen, når denne er eldre. */
    currentUrl: z.string().nullable(),
    contentHash: z.string(),
    body: z.array(legalBlockSchema).nullable(),
    /** Et nyere utkast av det samme dokumentet, når det finnes et og dette ikke er det. */
    draft: legalDraftSchema.nullable().default(null),
  })
  .meta({ id: 'LegalDocument' });
export type LegalDocumentDto = z.infer<typeof legalDocumentDtoSchema>;

const NUMBERED = /^[0-9]{1,3}\. /;

/**
 * Teksten i fila som blokker. Første linje (`# Tittel`) er dokumentets overskrift og tas ikke med —
 * tittelen står på raden. Linjer i samme avsnitt slås sammen med mellomrom.
 */
export function parseLegalText(text: string): LegalBlock[] {
  const blocks: LegalBlock[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];
  let ordered = false;
  let notice: string[] = [];
  let table: string[][] = [];
  const flush = (): void => {
    if (paragraph.length > 0) blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
    if (list.length > 0) blocks.push(ordered ? { kind: 'list', items: list, ordered } : { kind: 'list', items: list });
    if (notice.length > 0) blocks.push({ kind: 'notice', text: notice.join(' ') });
    const [header, ...rows] = table;
    if (header !== undefined) blocks.push({ kind: 'table', header, rows });
    paragraph = [];
    table = [];
    list = [];
    ordered = false;
    notice = [];
  };
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    if (line.trim() === '') {
      flush();
    } else if (line.startsWith('# ')) {
      flush();
    } else if (line.trimStart().startsWith('|')) {
      if (table.length === 0) flush();
      const cells = line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
      /* Skillelinja under overskriften (`| --- | --- |`) er ikke en rad. */
      if (!cells.every((cell) => /^:?-{3,}:?$/.test(cell))) table.push(cells);
    } else if (line.startsWith('### ')) {
      flush();
      blocks.push({ kind: 'heading', level: 3, text: line.slice(4).trim() });
    } else if (line.startsWith('## ')) {
      flush();
      blocks.push({ kind: 'heading', level: 2, text: line.slice(3).trim() });
    } else if (line.startsWith('- ')) {
      if (paragraph.length > 0 || notice.length > 0 || ordered) flush();
      list.push(line.slice(2).trim());
    } else if (NUMBERED.test(line)) {
      /* Uten denne ble «1. … 6. …» ett langt avsnitt (klikkrunden 27.10). */
      if (paragraph.length > 0 || notice.length > 0 || (list.length > 0 && !ordered)) flush();
      ordered = true;
      list.push(line.replace(NUMBERED, '').trim());
    } else if (line.startsWith('> ')) {
      if (paragraph.length > 0 || list.length > 0) flush();
      notice.push(line.slice(2).trim());
    } else if (list.length > 0) {
      /* En innrykket fortsettelse hører til punktet over. */
      list[list.length - 1] = `${list[list.length - 1] ?? ''} ${line.trim()}`;
    } else {
      paragraph.push(line.trim());
    }
  }
  flush();
  return blocks;
}
